import { differenceInCalendarDays, format, parseISO } from 'date-fns';

import type { Task } from '@/types/task';
import { localTodayISO, toLocalISODate } from '@/utils/date-utils';

export type TaskStatus = 'done' | 'overdue' | 'snoozed' | 'blocked' | 'pending';

/**
 * Derives the display status for a task based on its current state and today's date.
 */
export function getTaskStatus(task: Task, today: string = localTodayISO()): TaskStatus {
  if (task.completed) return 'done';
  if (task.blocked) return 'blocked';
  if (task.snoozedUntil && task.snoozedUntil >= today) return 'snoozed';
  if (task.dueDate < today) return 'overdue';
  return 'pending';
}

/**
 * Returns the CSS classes for the left border stripe on a task row.
 */
export function getStatusBorderClass(status: TaskStatus): string {
  switch (status) {
    case 'done':
      return 'border-l-[var(--color-done)]';
    case 'overdue':
      return 'border-l-[var(--color-overdue)]';
    case 'snoozed':
      return 'border-l-[var(--color-snoozed)]';
    case 'blocked':
      return 'border-l-[var(--color-border-subtle)]';
    case 'pending':
    default:
      return 'border-l-[var(--color-border)]';
  }
}

/**
 * Returns optional row background tint for status.
 * Overdue rows use the spine-wash token (--color-overdue-row-bg = rgb(233 107 68 / 0.05))
 * per M68 Operational Spine spec — subtle ember wash for task rows only.
 * --color-overdue-bg is reserved for banner/alert backgrounds (e.g. TaskDetailPage).
 */
export function getStatusRowBg(status: TaskStatus): string {
  switch (status) {
    case 'overdue':
      return 'bg-[var(--color-overdue-row-bg)]';
    case 'snoozed':
      return 'opacity-60';
    case 'blocked':
      return 'opacity-50';
    default:
      return '';
  }
}

/**
 * Formats a due date as a relative human-readable string.
 * e.g. "Today", "Tomorrow", "In 12 days", "3 days ago", "Yesterday"
 */
export function formatRelativeDueDate(dueDate: string, today: string = localTodayISO()): string {
  const diff = differenceInCalendarDays(parseISO(dueDate), parseISO(today));

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1) return `In ${diff} days`;
  return `${Math.abs(diff)} days ago`;
}

/**
 * Formats a due date as an absolute date string.
 * e.g. "Oct 7, 2026"
 */
export function formatAbsoluteDueDate(dueDate: string): string {
  return format(parseISO(dueDate), 'MMM d, yyyy');
}

/**
 * Formats a snoozedUntil date for display.
 * e.g. "Snoozed until Oct 14, 2026"
 */
export function formatSnoozeLabel(snoozedUntil: string): string {
  return `Snoozed until ${format(parseISO(snoozedUntil), 'MMM d, yyyy')}`;
}

/**
 * Checks whether a snooze is still active (snoozedUntil >= today).
 */
export function isSnoozeActive(snoozedUntil: string, today: string = localTodayISO()): boolean {
  return snoozedUntil >= today;
}

/**
 * Groups tasks by category, preserving sort order within each group.
 */
export function groupTasksByCategory<T extends { category?: string }>(
  tasks: T[],
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const task of tasks) {
    const key = task.category ?? 'other';
    const existing = groups.get(key);
    if (existing) {
      existing.push(task);
    } else {
      groups.set(key, [task]);
    }
  }
  return groups;
}

/**
 * Returns a compact due-date string for Prep task rows.
 * Handles done+past, blocked, snoozed, overdue, and future states.
 */
export function formatCompactDueDate(task: Task, today: Date): string {
  const todayStr = toLocalISODate(today);
  const status = getTaskStatus(task, todayStr);
  const dueDays = differenceInCalendarDays(parseISO(task.dueDate), today);

  if (status === 'done' && dueDays < 0) {
    return `${Math.abs(dueDays)} days ago`;
  }
  if (status === 'blocked') {
    return 'Blocked';
  }
  if (status === 'snoozed') {
    if (task.snoozedUntil) {
      const remaining = differenceInCalendarDays(parseISO(task.snoozedUntil), today);
      const days = Math.max(0, remaining);
      return `Snoozed ${days}d`;
    }
    return 'Snoozed 0d';
  }
  if (status === 'overdue') {
    return `${Math.abs(dueDays)}d late`;
  }
  if (dueDays === 0) return 'Today';
  if (dueDays === 1) return 'Tomorrow';
  if (dueDays > 1) return `In ${dueDays} days`;
  // done + same day or done + future
  return `${Math.abs(dueDays)} days ago`;
}

/**
 * Returns the CSS color value for the due-meta label on a Prep task row.
 */
export function getDueMetaColorVar(status: TaskStatus | 'done'): string {
  switch (status) {
    case 'overdue':
      return 'var(--color-accent-hover)';
    case 'snoozed':
      return '#5a7a8a';
    case 'blocked':
      return '#6e6a63';
    default:
      return 'var(--color-text-muted)';
  }
}
